import React from 'react';
import cx from 'classnames';
import { track } from '@trello/analytics';
import {
  useSelectCoverQuery,
  useUpdateCardCoverMutation,
} from '@trello/graphql/generated';
import { forTemplate } from 'app/common/lib/util/i18n';
import {
  Analytics,
  Action,
  Source,
  ActionSubject,
  ActionSubjectId,
} from '@trello/atlassian-analytics';

import styles from './ColorCover.less';

const format = forTemplate('card_cover_chooser');

interface ColorCoverProps {
  cardId: string;
}

export const cardCoverColors = [
  'green',
  'yellow',
  'orange',
  'red',
  'purple',
  'blue',
  'sky',
  'lime',
  'pink',
  'black',
];

export const ColorCover: React.FunctionComponent<ColorCoverProps> = ({
  cardId,
}) => {
  const { data } = useSelectCoverQuery({
    variables: { cardId },
    fetchPolicy: 'cache-only',
  });
  const [updateCardCover] = useUpdateCardCoverMutation();

  const card = data?.card;
  const currentCover = card?.cover;
  const selectedColor = currentCover?.color;

  const containers = {
    organization: {
      id: card?.board.idOrganization,
    },
    board: {
      id: card?.board.id,
    },
    card: {
      id: card?.id,
    },
  };

  const setCover = async (color: string) => {
    let cover: null | {
      color: string;
      brightness: string;
    } = {
      color,
      brightness: color === 'black' ? 'dark' : 'light',
    };

    if (color === selectedColor) {
      cover = null;
      track('Card Cover Chooser', 'Toggled Cover Off', 'Color');
      Analytics.sendTrackEvent({
        action: Action.removed,
        actionSubject: ActionSubject.cover,
        source: Source.cardCoverInlineDialog,
        attributes: {
          type: 'color',
        },
        containers,
      });
    } else {
      track('Card Cover Chooser', 'Chose Cover', 'Color');
      Analytics.sendTrackEvent({
        action: Action.updated,
        actionSubject: ActionSubject.cover,
        source: Source.cardCoverInlineDialog,
        attributes: {
          type: 'color',
          value: color,
        },
        containers,
      });
    }

    await updateCardCover({
      variables: {
        cardId,
        cover,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateCardCover: {
          __typename: 'Card',
          id: cardId,
          cover: cover
            ? {
                __typename: 'Card_Cover',
                ...currentCover,
                color,
                brightness: cover.brightness,
                idUploadedBackground: null,
                idAttachment: null,
              }
            : {
                __typename: 'Card_Cover',
                ...currentCover,
                color: null,
                idUploadedBackground: null,
                idAttachment: null,
                scaled: null,
              },
          idAttachmentCover: null,
        },
      },
    });
  };

  return (
    <>
      <h4 className={styles.heading}>{format('colors')}</h4>
      <div className={styles.colorTiles}>
        {cardCoverColors.map((color) => (
          <button
            key={color}
            onClick={() => {
              Analytics.sendUIEvent({
                action: Action.clicked,
                actionSubject: ActionSubject.tile,
                actionSubjectId: ActionSubjectId.cardCoverColorTile,
                source: Source.cardCoverInlineDialog,
                attributes: {
                  value: color,
                },
                containers,
              });
              setCover(color);
            }}
            className={cx(styles.colorTile, styles[color], {
              [styles.selected]: color === selectedColor,
            })}
          />
        ))}
      </div>
    </>
  );
};
